import { UseFormReturn } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { isHttps } from "@/components/reusables/FileUpload";
import { DailyReportTostMessage } from "@/constants/daily-report.constants";
import {
  useAddReportMutation,
  useUpdateReportMutation,
} from "@/redux/api/reportsQuery";
import { rosterApi } from "@/redux/api/rosterQuery";
import { TDailyReport } from "@/schemas/daily-report.schema";
import { customToast } from "@/utils/custom-toast";
import { toUTC } from "@/utils/helper-functions/date-time.helper";
import {
  parseToFormData,
  validateResponse,
} from "@/utils/helper-functions/form.helper";

interface UseDailyReportMutation {
  id?: string;
  form: UseFormReturn<TDailyReport>;
  daySheetIds?: string[];
}

interface UseDailyReportMutatationType {
  mutateDailyReport: (data: TDailyReport) => Promise<void>;
  navigateBack: () => void;
  isMutating: boolean;
}

export const useDailyReportMutation = (
  props: UseDailyReportMutation,
): UseDailyReportMutatationType => {
  const navigate = useNavigate();
  const { id, form, daySheetIds = [] } = props;
  const [addReport, { isLoading }] = useAddReportMutation();
  const [updateReport, { isLoading: isUpdating }] = useUpdateReportMutation();
  const dispatch = useDispatch();

  const navigateBack = (): void => {
    if (daySheetIds.length > 0) {
      navigate("/");

      return;
    }
    navigate("/reports/daily-reports");
  };

  const mutateDailyReport = async (data: TDailyReport): Promise<void> => {
    // Check if there is weighScaleticketId
    const filteredManualData =
      data?.resourceRecoveryDetails?.manualData?.filter(
        (single) => !single.tippingId,
      );

    const weighScaleTicketsIds =
      data?.resourceRecoveryDetails?.manualData
        ?.filter((single) => single.tippingId)
        ?.map((single) => single.tippingId) || [];

    const initialUsedTickets =
      form.formState.defaultValues?.resourceRecoveryDetails?.usedTickets;

    const deletedUsedTickets =
      initialUsedTickets?.filter(
        (single) => !weighScaleTicketsIds.includes(single),
      ) || [];

    const manualData = filteredManualData?.map((single) => {
      const filteredWasteTypeArray =
        single?.breakDown?.mixedWaste?.filter(
          (waste) =>
            waste?.wasteTypeId !== "" &&
            waste?.percentage !== 0 &&
            waste?.wasteName !== "",
        ) || [];

      const otherWasteTypeArray =
        single?.breakDown?.otherWaste?.filter(
          (waste) => waste?.wasteTypeId !== "" && waste?.wasteName !== "",
        ) || [];
      single.breakDown.mixedWaste = filteredWasteTypeArray;
      single.breakDown.otherWaste = otherWasteTypeArray;

      return single;
    });

    const { employeeWorkingHours, subcontractorWorkingHours } = data;

    let res;

    const formData = parseToFormData({
      ...data,
      date: toUTC(data.date as Date | string),
      resourceRecoveryDetails: {
        ...data.resourceRecoveryDetails,
        usedTickets: [...new Set(weighScaleTicketsIds)],
        manualData,
        deletedUsedTickets,
      },
      employeeWorkingHours:
        employeeWorkingHours
          ?.map((single) => ({
            ...single,
            files: [],
            existingFiles: single?.files?.length
              ? single?.files.filter(
                  (file) => !(file instanceof File) && isHttps(file as string),
                )
              : [],
          }))
          ?.filter((single) => single.employeeId !== "") || [],
      subcontractorWorkingHours:
        subcontractorWorkingHours
          ?.map((single) => ({
            ...single,
            files: [],
            existingFiles: single?.files?.length
              ? single?.files.filter(
                  (file) => !(file instanceof File) && isHttps(file as string),
                )
              : [],
          }))
          ?.filter((single) => single.subcontractorId !== "") || [],
    });

    //Check if there is images in employeeWorkingHours
    employeeWorkingHours?.forEach((employee, index) => {
      if (employee?.files?.length) {
        employee?.files?.forEach((image) => {
          if (image instanceof File) {
            formData.append(
              `employeeWorkingHours.${index}.files`,
              image as File,
            );
          }
        });
      }
    });

    //Check if there is images in subcontractorWorkingHours
    subcontractorWorkingHours?.forEach((subcontractor, index) => {
      if (subcontractor?.files?.length) {
        subcontractor?.files?.forEach((image) => {
          if (image instanceof File) {
            formData.append(
              `subcontractorWorkingHours.${index}.files`,
              image as File,
            );
          }
        });
      }
    });

    if (!id) {
      formData.append("daySheetIds", JSON.stringify(daySheetIds));
    }

    if (id) {
      res = await updateReport({
        data: formData,
        id,
      });
    } else {
      res = await addReport({
        data: formData,
      });
    }

    validateResponse({
      response: res,
      onSuccess: () => {
        customToast.success(
          id
            ? DailyReportTostMessage.SUCCESS.UPDATED
            : DailyReportTostMessage.SUCCESS.CREATED,
        );
        if (daySheetIds.length > 0) {
          dispatch(
            rosterApi.util.invalidateTags([
              "daySheet",
              "allocated",
              "fleet",
              "singleDaySheet",
            ]),
          );
        }

        navigateBack();
        form.reset();
      },
    });
  };

  return {
    mutateDailyReport,
    navigateBack,
    isMutating: isLoading || isUpdating,
  };
};
